import type { LucideIcon } from 'lucide-react';

import { Card, CardContent } from './card';
import { cn } from '../../lib/utils';

export type KpiAccent = 'emerald' | 'sky' | 'violet' | 'amber' | 'rose' | 'indigo';

const ACCENT_STYLES: Record<KpiAccent, string> = {
  emerald: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  sky: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
  violet: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-500',
  rose: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
  indigo: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
};

const TREND_STYLES = {
  up: 'text-emerald-600 dark:text-emerald-400',
  down: 'text-rose-600 dark:text-rose-400',
  neutral: 'text-muted-foreground',
} as const;

export interface KpiCardProps {
  label: string;
  value: string;
  hint?: string;
  icon: LucideIcon;
  accent?: KpiAccent;
  trend?: {
    direction: 'up' | 'down' | 'neutral';
    label: string;
  };
  className?: string;
}

export function KpiCard({
  label,
  value,
  hint,
  icon: Icon,
  accent = 'indigo',
  trend,
  className,
}: KpiCardProps) {
  return (
    <Card
      className={cn(
        'gap-3 py-5 transition-shadow hover:shadow-md',
        className,
      )}
    >
      <CardContent className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1 min-w-0">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </span>
          <span className="text-2xl font-semibold tracking-tight truncate">
            {value}
          </span>
          {(hint || trend) && (
            <div className="flex items-center gap-2 text-xs">
              {trend && (
                <span className={cn('font-medium', TREND_STYLES[trend.direction])}>
                  {trend.direction === 'up' && '▲ '}
                  {trend.direction === 'down' && '▼ '}
                  {trend.label}
                </span>
              )}
              {hint && <span className="text-muted-foreground">{hint}</span>}
            </div>
          )}
        </div>
        <div
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-xl',
            ACCENT_STYLES[accent],
          )}
        >
          <Icon className="size-5" />
        </div>
      </CardContent>
    </Card>
  );
}
